<template>
  <div class="video-translation-view">
    <!-- Toast 提示组件 -->
    <div v-if="toast.show" class="toast-container" :class="toast.type">
      <div class="toast-content">
        <span class="toast-icon">{{ toast.icon }}</span>
        <span class="toast-message">{{ toast.message }}</span>
      </div>
    </div>

    <!-- 视频翻译主界面 -->
    <div class="video-translation">
      <h2>视频翻译</h2>
      <div class="notice-text">
        注意：当前视频对口型仅能解决全程单人出镜且每一帧都有正脸的视频，无法解决多人、转头、面部遮挡或无脸画面。视频翻译的准确性受字幕准确率影响。
      </div>
      <div class="subtitle-desc">
        视频字幕生成、字幕翻译、语音翻译
      </div>

      <!-- ==================== 一键翻译模式 ==================== -->
      <div class="content-area">
        <div class="left-area">
          <div class="video-container">
            <div
              v-if="!selectedFile"
              class="upload-box"
              @click="triggerFileUpload"
              @drop.prevent="handleFileDrop"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              :class="{ 'dragging': isDragging }"
            >
              <div class="upload-icon">+</div>
              <p>点击上传或拖拽本人素材</p>
              <small>上传视频时长最长不超过 60 分钟</small>
              <input
                type="file"
                ref="fileInput"
                @change="handleFileSelect"
                accept="video/*"
                style="display: none;"
              >
            </div>
            <div v-else-if="selectedFile" class="video-player-wrapper">
              <button class="delete-video-btn" @click="confirmDelete" aria-label="删除已上传的视频">×</button>
              <video ref="videoPlayer" controls @loadedmetadata="getVideoMetadata">
                <source :src="videoUrl" type="video/mp4">
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
        <div class="right-container">
          <div class="setting-card">
            <div class="setting-content">
              <h4>视频翻译成：</h4>
              <p>将视频翻译成什么语言</p>
            </div>
            <div class="setting-control">
              <div class="select-wrapper">
                <select v-model="targetLanguage">
                  <option value="en">英文</option>
                  <option value="zh">中文</option>
                </select>
              </div>
            </div>
          </div>
          <div class="setting-card">
            <div class="setting-content">
              <h4>返回字幕类型</h4>
              <p>选择是否添加字幕以及字幕语言类型</p>
            </div>
            <div class="setting-control">
              <div class="select-wrapper">
                <select v-model="subtitleOption">
                  <option value="none">不添加字幕</option>
                  <option value="source">原始语言字幕</option>
                  <option value="target">目标语言字幕</option>
                  <option value="bilingual">双语字幕</option>
                </select>
              </div>
            </div>
          </div>
          <div class="setting-card">
            <div class="setting-content">
              <h4>是否写入硬字幕</h4>
              <p>将翻译字幕烧录/嵌入到视频画面中，生成后不可关闭</p>
            </div>
            <div class="setting-control">
              <label class="toggle-switch">
                <input type="checkbox" v-model="hardSubtitle">
                <span class="toggle-slider"></span>
              </label>
            </div>
          </div>
          <!-- 新增：是否对口型 -->
          <div class="setting-card">
            <div class="setting-content">
              <h4>是否对口型</h4>
              <p>生成视频时同步调整口型以匹配翻译后的语音</p>
            </div>
            <div class="setting-control">
              <label class="toggle-switch">
                <input type="checkbox" v-model="lipSync">
                <span class="toggle-slider"></span>
              </label>
            </div>
          </div>
          <div class="action-buttons">
            <button
              class="translate-button"
              @click="startOneClickTranslation"
              :disabled="!selectedFile || videoDuration > 3600"
            >
              开始一键翻译
            </button>
          </div>
        </div>
      </div>

      <p class="terms">
        仅对本人视频翻译，您承诺您的素材内容为您本人所有或已获得合法授权。并遵守《音视频翻译功能使用规范》
      </p>
    </div>

    <!-- 删除确认对话框 -->
    <div v-if="showDeleteConfirm" class="confirm-dialog-overlay">
      <div class="confirm-dialog">
        <h3>确认删除</h3>
        <p v-if="deleteMode === 'video'">您确定要删除已上传的视频吗？此操作无法撤销。</p>
        <p v-else>您确定要执行删除操作吗？此操作无法撤销。</p>
        <div class="confirm-dialog-buttons">
          <button class="cancel-btn" @click="showDeleteConfirm = false">取消</button>
          <button class="confirm-btn" @click="handleDeleteConfirm">删除</button>
        </div>
      </div>
    </div>

    <!-- 确认收费对话框 -->
    <div v-if="showConfirmPricing" class="confirm-dialog-overlay">
      <div class="confirm-dialog">
        <h3>确认费用</h3>
        <p>
          视频音频时长 {{ videoDurationMinutes.toFixed(2) }} 分钟，
          按 {{ currentRate }} 元/分钟计费，总计 {{ confirmCost.toFixed(2) }} 元。
        </p>
        <div class="confirm-dialog-buttons">
          <button class="cancel-btn" @click="showConfirmPricing = false">取消</button>
          <button class="confirm-btn" @click="handleConfirmPricing">确定</button>
        </div>
      </div>
    </div>

    <!-- 处理中提示 -->
    <div v-if="isProcessing" class="processing-overlay">
      <div class="processing-dialog">
        <div class="spinner"></div>
        <h3>{{ processingMessage }}</h3>

        <!-- 上传进度：大文件上传耗时较长，这里给出真实进度，避免被误认为卡死 -->
        <div v-if="isUploading" class="upload-progress">
          <div class="upload-progress-track">
            <div class="upload-progress-bar" :style="{ width: uploadPercent + '%' }"></div>
          </div>
          <p class="upload-progress-text">
            {{ uploadPercent.toFixed(1) }}% · {{ uploadLoadedText }} · {{ uploadSpeedText }}
          </p>
          <p v-if="uploadSlowTip" class="upload-slow-tip">{{ uploadSlowTip }}</p>
          <button class="cancel-upload-btn" @click="cancelUpload">取消上传</button>
        </div>
        <p v-else>请求已提交，正在排队处理...</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getUpQiniuToken,
  uploadToQiniu,
  saveoneclickVideoTranslateFileUrl
} from '@/api/upfile'
import { QINIU_BASE_URL } from '@/api/constants'
import { mapGetters } from 'vuex'
import uploadProgressMixin from '@/utils/uploadProgressMixin'

export default {
  name: 'VideoTranslationWithRecords',
  mixins: [uploadProgressMixin],
  data () {
    return {
      // 一键翻译相关
      targetLanguage: 'en',
      subtitleOption: 'target',
      hardSubtitle: false,
      lipSync: false,
      selectedFile: null,
      videoUrl: '',
      videoDuration: 0,
      isDragging: false,
      // 删除相关
      hasAddedJob: false,
      showDeleteConfirm: false,
      showConfirmPricing: false,
      confirmCost: 0,
      isProcessing: false,
      processingMessage: '',
      deleteMode: null,
      toast: {
        show: false,
        message: '',
        type: 'success',
        icon: '✓'
      },
      // 💰 价格配置（单位：元/分钟）
      pricing: {
        standard: 2,
        lipSync: 4
      }
    }
  },
  computed: {
    ...mapGetters(['balance', 'isLoggedIn']),
    currentRate () {
      return this.lipSync ? this.pricing.lipSync : this.pricing.standard
    },
    videoDurationMinutes () {
      return this.videoDuration / 60
    },
    totalCost () {
      return (this.videoDuration / 60) * this.currentRate
    }
  },
  methods: {
    // ─── Toast ────────────────────────────────────────────
    showToast (message, type = 'success') {
      this.toast.message = message
      this.toast.type = type
      this.toast.icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'
      this.toast.show = true
      setTimeout(() => { this.toast.show = false }, 3000)
    },
    showError (message) { this.showToast(message, 'error') },
    showSuccess (message) { this.showToast(message, 'success') },

    // ─── 工具 ─────────────────────────────────────────────
    generateQiniuFileName (originalFilename = 'video.mp4') {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      const milliseconds = String(now.getMilliseconds()).padStart(3, '0')
      const randomMicro = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
      const microSeconds = milliseconds + randomMicro
      let uuidShort
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        uuidShort = crypto.randomUUID().replace(/-/g, '').slice(0, 4)
      } else {
        uuidShort = Math.random().toString(16).substring(2, 6)
      }
      const ext = originalFilename.split('.').pop() || 'mp4'
      return `oneclickVideoTranslation_${year}${month}${day}_${hours}${minutes}${seconds}_${microSeconds}_${uuidShort}.${ext}`
    },

    // ─── 一键翻译文件处理 ──────────────────────────────────
    triggerFileUpload () {
      this.$refs.fileInput.click()
    },
    handleFileSelect (event) {
      const file = event.target.files[0]
      if (file && file.type.startsWith('video/')) {
        this.processVideoFile(file)
      } else if (file) {
        this.showError('请选择有效的视频文件')
      }
    },
    handleFileDrop (event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('video/')) {
        this.processVideoFile(file)
      } else if (file) {
        this.showError('请拖拽有效的视频文件')
      }
    },
    processVideoFile (file) {
      // 放宽至 2000MB (2GB) 以支持 60 分钟的视频文件
      if (file.size > 2000 * 1024 * 1024) {
        this.showError('文件大小超过限制（最大 2000MB）')
        return
      }
      this.selectedFile = file
      this.videoUrl = URL.createObjectURL(file)
      this.hasAddedJob = false
    },
    getVideoMetadata () {
      const video = this.$refs.videoPlayer
      this.videoDuration = video.duration
      if (video.duration > 3600) {
        this.showError('视频时长超过限制（最大 60 分钟）')
        this.resetFileInput()
      }
    },

    // ─── 删除逻辑 ──────────────────────────────────────────
    confirmDelete () {
      this.deleteMode = 'video'
      this.showDeleteConfirm = true
    },
    deleteVideo () {
      this.selectedFile = null
      this.videoUrl = ''
      this.videoDuration = 0
      this.showDeleteConfirm = false
      this.hasAddedJob = false
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    async handleDeleteConfirm () {
      this.showDeleteConfirm = false
      if (this.deleteMode === 'video') {
        this.deleteVideo()
      }
      this.deleteMode = null
    },

    // ─── 收费确认 ──────────────────────────────────────────
    handleConfirmPricing () {
      this.showConfirmPricing = false
      this.confirmAndStartOneClickTranslation()
    },

    // ─── 一键翻译 ──────────────────────────────────────────
    async startOneClickTranslation () {
      if (!this.isLoggedIn) { this.showError('请先登录'); return }
      if (!this.selectedFile) { this.showError('请先选择一个视频文件'); return }
      if (this.videoDuration > 3600) { this.showError('视频时长不能超过 60 分钟'); return }
      if (this.balance < this.totalCost) {
        this.showError(`余额不足，当前余额 ¥${this.balance}，所需 ¥${this.totalCost.toFixed(2)}`)
        return
      }
      this.confirmCost = this.totalCost
      this.showConfirmPricing = true
    },
    async confirmAndStartOneClickTranslation () {
      this.isProcessing = true
      this.processingMessage = '正在上传视频并启动翻译...'
      const file = this.selectedFile
      const originalFilename = file.name
      const uploadFileName = this.generateQiniuFileName(file.name)
      const key = uploadFileName
      this.beginUploadProgress(file.size)
      try {
        // 传入 key：后端会把上传凭证限定到该文件，并使用更长的有效期
        const tokenResponse = await getUpQiniuToken(key)
        if (tokenResponse.status === 401) {
          this.showError('身份验证失败，请重新登录')
          return
        }
        const qiniuUploadToken = tokenResponse.uploadToken
        const response = await uploadToQiniu(file, key, qiniuUploadToken, this.createUploadHandlers())
        this.isUploading = false
        this.processingMessage = '视频上传完成，正在启动翻译...'
        const fileUrl = `${QINIU_BASE_URL}/${response.data.key}`
        const translationOptions = {
          operation_type: 'oneclickVideoTranslation',
          input_file_url: fileUrl,
          input_filename: originalFilename,
          target_lang: this.targetLanguage,
          subtitleOption: this.subtitleOption,
          hardSubtitle: this.hardSubtitle,
          lipSync: this.lipSync,
          audio_duration: this.videoDuration
        }
        const mergeResponse = await saveoneclickVideoTranslateFileUrl(translationOptions)
        if (mergeResponse && mergeResponse.status === 200) {
          if (typeof mergeResponse.balance !== 'undefined') {
            this.$store.commit('setBalance', mergeResponse.balance)
          }
          this.showSuccess(`视频上传成功，翻译处理已开始。本次处理费用：${this.totalCost.toFixed(2)} 元`)
          this.resetFileInput()
        } else {
          this.showError(mergeResponse.message || '服务端返回异常，请稍后重试')
        }
      } catch (error) {
        if (this.isUploadCanceledError(error)) {
          this.showToast('已取消上传', 'info')
        } else if (error && error.response) {
          // 已拿到 HTTP 响应：多为凭证/参数问题
          this.showError(`上传失败（HTTP ${error.response.status}），请刷新后重试`)
        } else if (error && error.message) {
          this.showError(error.message)
        } else {
          this.showError('网络错误或服务异常，请稍后重试')
        }
      } finally {
        this.endUploadProgress()
        this.isProcessing = false
      }
    },
    resetFileInput () {
      this.selectedFile = null
      if (this.videoUrl) {
        URL.revokeObjectURL(this.videoUrl)
        this.videoUrl = ''
      }
      this.videoDuration = 0
      this.hasAddedJob = false
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    }
  }
}
</script>

<style scoped>
/* ========== 全局容器 ========== */
.video-translation-view {
  width: 780px;
  min-height: auto;
  background-color: #1a1a1a;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* ========== Toast ========== */
.toast-container {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  min-width: 300px;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}

.toast-container.success { background-color: #4caf50; color: white; }
.toast-container.error   { background-color: #f44336; color: white; }
.toast-container.info    { background-color: #2196f3; color: white; }

.toast-content { display: flex; align-items: center; gap: 12px; font-size: 14px; font-weight: 500; }
.toast-icon    { font-size: 20px; font-weight: bold; }
.toast-message { flex: 1; }

/* ========== 主容器 ========== */
.video-translation {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
  max-width: 1200px;
  width: 780px;
  min-width: 780px;
  margin: 0 auto;
  padding: 10px;
  color: white;
  background-color: #1a1a1a;
  height: auto;
}

h2 { text-align: center; margin: 10px 0 5px; font-size: 20px; font-weight: 600; }

/* ========== 新增提示小字样式 ========== */
.notice-text {
  font-size: 12px;
  color: #ffb74d;
  text-align: center;
  margin: 0 0 10px 0;
  line-height: 1.5;
  padding: 0 15px;
}

.subtitle-desc { margin-bottom: 15px; color: #bbb; font-size: 15px; text-align: center; }

/* ========== 一键翻译 ========== */
.content-area {
  display: flex; gap: 20px; align-items: flex-start;
  justify-content: center; margin-top: 12px;
}

.left-area, .right-container { width: 350px; height: 350px; }

.video-container {
  width: 100%; height: 100%; background-color: #2a2a2a;
  border-radius: 12px; position: relative;
}

.right-container {
  display: flex; flex-direction: column; gap: 12px;
  overflow-y: auto; padding-right: 4px;
}

.upload-box {
  width: 100%; height: 100%;
  display: flex; flex-direction: column;
  justify-content: center; align-items: center;
  border: 2px dashed #555; border-radius: 12px;
  cursor: pointer; transition: all 0.3s ease;
}

.upload-box:hover, .upload-box.dragging {
  background-color: rgba(0, 188, 212, 0.1); border-color: #00bcd4;
}

.upload-icon { font-size: 48px; color: #00bcd4; margin-bottom: 12px; }
.upload-box p     { font-size: 14px; margin: 4px 0; }
.upload-box small { font-size: 12px; color: #999; }

.video-player-wrapper {
  width: 100%; height: 100%; position: relative;
  border: 2px solid #00bcd4; border-radius: 12px; box-sizing: border-box;
}

.delete-video-btn {
  position: absolute; top: 3px; right: 3px;
  width: 20px; height: 20px; border-radius: 50%;
  background-color: rgba(244, 67, 54, 0.9); border: none;
  color: white; font-size: 20px; cursor: pointer; z-index: 20;
  transition: all 0.2s; display: flex; align-items: center; justify-content: center;
}

.delete-video-btn:hover { background-color: #f44336; transform: scale(1.1); }

video {
  width: 100%; height: 100%;
  object-fit: contain; background-color: #1a1a1a;
}

/* ─── 设置卡片 ─── */
.setting-card {
  background-color: #2a2a2a; border-radius: 8px; padding: 8px;
  display: flex; align-items: center; gap: 8px;
  transition: background-color 0.2s; cursor: pointer;
}

.setting-card:hover { background-color: #333; }

.setting-content { flex: 1; }
.setting-content h4 { font-size: 12px; font-weight: 400; margin: 0 0 4px 0; }
.setting-content p  { font-size: 10px; color: #999; margin: 0; line-height: 1.1; }

.setting-control { flex-shrink: 0; display: flex; align-items: center; }

.select-wrapper { position: relative; min-width: 130px; }

.select-wrapper select {
  width: 100%; padding: 8px 32px 8px 12px;
  background-color: #333; color: white;
  border: 1px solid #555; border-radius: 6px;
  appearance: none; cursor: pointer; font-size: 13px;
}

.select-wrapper select:hover { border-color: #00bcd4; }

.toggle-switch {
  position: relative; display: inline-block; width: 38px; height: 15px;
}

.toggle-switch input { opacity: 0; width: 0; height: 0; }

.toggle-slider {
  position: absolute; cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #555; transition: .3s; border-radius: 14px;
}

.toggle-slider:before {
  position: absolute; content: "";
  height: 12px; width: 12px; left: 1.5px; bottom: 1.5px;
  background-color: white; transition: .3s; border-radius: 50%;
}

input:checked + .toggle-slider { background-color: #00bcd4; }

input:checked + .toggle-slider:before { transform: translateX(24px); }

.action-buttons { margin-top: auto; }

.translate-button {
  width: 100%; padding: 8px 0;
  background: linear-gradient(135deg, #00bcd4, #0097a7);
  color: white; border: none; border-radius: 8px;
  cursor: pointer; font-size: 12px; font-weight: 500;
  transition: all 0.2s; box-shadow: 0 4px 12px rgba(0, 188, 212, 0.3);
}

.translate-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #0097a7, #00838f);
  transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0, 188, 212, 0.4);
}

.translate-button:disabled { background: #555; cursor: not-allowed; box-shadow: none; transform: none; }

.terms { font-size: 12px; color: #888; margin-top: 15px; text-align: center; line-height: 0.5; }

/* ========== 对话框 ========== */
.confirm-dialog-overlay,
.processing-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}

.confirm-dialog, .processing-dialog {
  background-color: #2a2a2a; border-radius: 16px;
  width: 300px; color: white; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
}

.confirm-dialog { padding: 8px; }
.confirm-dialog h3 { font-size: 15px; margin: 0 0 10px; font-weight: 550; }
.confirm-dialog p  { margin: 0 0 12px; color: #ccc; line-height: 1.1; }
.confirm-dialog-buttons { display: flex; justify-content: flex-end; gap: 12px; }

.cancel-btn {
  background-color: transparent; border: 1px solid #666; color: #ccc;
  padding: 8px 16px; border-radius: 8px; cursor: pointer;
  font-size: 10px; font-weight: 500; transition: all 0.2s;
}

.cancel-btn:hover { background-color: rgba(255,255,255,0.1); border-color: #999; color: white; }

.confirm-btn {
  background-color: #f44336; color: white; border: none;
  padding: 8px 16px; border-radius: 8px; cursor: pointer;
  font-size: 10px; font-weight: 500; transition: all 0.2s;
}

.confirm-btn:hover { background-color: #d32f2f; transform: translateY(-2px); }

.processing-dialog { padding: 40px; text-align: center; }

.spinner {
  width: 60px; height: 60px;
  border: 4px solid #444; border-top-color: #00bcd4;
  border-radius: 50%; animation: spin 1s linear infinite;
  margin: 0 auto 24px;
}

@keyframes spin { to { transform: rotate(360deg); } }

.processing-dialog h3 { margin: 0 0 12px; font-size: 18px; font-weight: 600; }
.processing-dialog p  { margin: 0; color: #999; font-size: 14px; }

/* 上传进度条 + 取消按钮 */
.upload-progress { margin-top: 4px; }

.upload-progress-track {
  width: 100%; height: 8px; border-radius: 4px;
  background-color: #444; overflow: hidden;
}

.upload-progress-bar {
  height: 100%; border-radius: 4px;
  background: linear-gradient(90deg, #00bcd4, #0097a7);
  transition: width 0.2s ease-out;
}

.upload-progress-text { margin: 8px 0 0; color: #ccc; font-size: 12px; }
.upload-slow-tip      { margin: 6px 0 0; color: #ffb74d; font-size: 11px; line-height: 1.4; }

.cancel-upload-btn {
  margin-top: 14px; padding: 6px 16px;
  background-color: transparent; border: 1px solid #666; color: #ccc;
  border-radius: 8px; cursor: pointer; font-size: 12px; transition: all 0.2s;
}

.cancel-upload-btn:hover { background-color: rgba(255, 255, 255, 0.1); border-color: #999; color: white; }

/* ========== 移动端 ========== */
@media screen and (max-width: 768px) {
  .toast-container { min-width: 250px; padding: 12px 16px; top: 60px; }
  .toast-content   { font-size: 12px; }
  .toast-icon      { font-size: 16px; }

  .video-translation::before {
    content: "👉 可左右滑动查看完整内容";
    display: block; text-align: center; color: #00bcd4;
    font-size: 12px; padding: 8px;
    background-color: rgba(0, 188, 212, 0.1);
    border-radius: 4px; margin-bottom: 10px;
  }
}
</style>
